import React, { useMemo } from "react";
import moment from "moment/moment";
import styled, { css } from "styled-components";
import { TIME_UNITS } from "components/event-definitions/event-definition-types/FilterForm";
import { getPriorityName } from "logic/alerts/EventDefinitionPriorityEnum";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { Timestamp, HoverForHelp } from "components/common";
import useReplaySearchContext from "components/event-definitions/replay-search/hooks/useReplaySearchContext";
import useFeature from "hooks/useFeature";
import SidebarNavigationLink from "components/layout/RightSidebar/SidebarNavigationLink";
import SidebarEventDefinitionDetails from "components/event-definitions/SidebarEventDefinitionDetails";
import Routes from "routing/Routes";
import Link from "components/common/Link";
import useAlertAndEventDefinitionData from "./useAlertAndEventDefinitionData";
import AggregationConditions from "../AggregationConditions";
import Notifications from "../Notifications";
const AlertTimestamp = styled(Timestamp)(
  ({ theme }) => css`
    color: ${theme.colors.variant.darker.warning};
  `
);
const EventDefinitionTitle = () => {
  const { alertId, definitionId } = useReplaySearchContext();
  const { eventDefinition } = useAlertAndEventDefinitionData(alertId, definitionId);
  return alertId ? /* @__PURE__ */ React.createElement(SidebarNavigationLink, { content: SidebarEventDefinitionDetails(definitionId) }, eventDefinition.title) : /* @__PURE__ */ React.createElement(Link, { to: Routes.ALERTS.DEFINITIONS.show(definitionId) }, eventDefinition.title);
};
const useAttributeComponents = () => {
  const { alertId, definitionId, type } = useReplaySearchContext();
  const { eventData, eventDefinition } = useAlertAndEventDefinitionData(alertId, definitionId);
  const isRightSidebarEnabled = useFeature("replay_search_right_sidebar");
  return useMemo(() => {
    const isEventDefinition = type === "event_definition";
    if (!eventDefinition) {
      return [{ title: "Timestamp", content: /* @__PURE__ */ React.createElement(Timestamp, { dateTime: eventData == null ? void 0 : eventData.timestamp }), show: !isEventDefinition }];
    }
    const searchWithin = extractDurationAndUnit(eventDefinition.config.search_within_ms, TIME_UNITS);
    const executeEvery = extractDurationAndUnit(eventDefinition.config.execute_every_ms, TIME_UNITS);
    const isEDUpdatedAfterEvent = !isEventDefinition && moment(eventDefinition.updated_at).diff(eventData == null ? void 0 : eventData.timestamp) > 0;
    const components = [
      {
        title: "Event definition updated at",
        content: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(AlertTimestamp, { dateTime: eventDefinition.updated_at }), /* @__PURE__ */ React.createElement(HoverForHelp, { displayLeftMargin: true, iconSize: "xs" }, "Event definition ", /* @__PURE__ */ React.createElement("i", null, eventDefinition.title), " was edited after this event happened. Some of aggregations widgets might not be representative for this event.")),
        show: isEDUpdatedAfterEvent
      },
      {
        title: "Event definition",
        content: /* @__PURE__ */ React.createElement(EventDefinitionTitle, null),
        show: !isEventDefinition
      },
      {
        title: "Priority",
        content: getPriorityName(eventDefinition.priority)
      },
      {
        title: "Execute search every",
        content: (executeEvery == null ? void 0 : executeEvery.duration) && (executeEvery == null ? void 0 : executeEvery.unit) && `${executeEvery.duration} ${executeEvery.unit.toLowerCase()}`
      },
      {
        title: "Search within",
        content: (searchWithin == null ? void 0 : searchWithin.duration) && (searchWithin == null ? void 0 : searchWithin.unit) && `${searchWithin.duration} ${searchWithin.unit.toLowerCase()}`
      },
      {
        title: "Notifications",
        content: /* @__PURE__ */ React.createElement(Notifications, null)
      },
      { title: "Description", content: eventDefinition.description }
    ];
    if (!isRightSidebarEnabled) {
      components.push({
        title: "Aggregation conditions",
        content: /* @__PURE__ */ React.createElement(AggregationConditions, null)
      });
    }
    return components;
  }, [eventData == null ? void 0 : eventData.timestamp, eventDefinition, isRightSidebarEnabled, type]);
};
export default useAttributeComponents;
